/**
 * Trainer scale path:
 * - Middle stage: always pull cams for listed identities
 * - Right rail: pull cam only when that student has camera enabled (live tile)
 * - Cam off on rail → avatar only (no subscribe)
 * - Screen shares always; speaker audio without forcing camera
 */

import { useEffect, useMemo } from "react";
import {
  useMaybeRoomContext,
  useSpeakingParticipants,
} from "@livekit/components-react";
import {
  RemoteTrackPublication,
  RoomEvent,
  Track,
} from "livekit-client";
import { isParticipantPresenter, parseParticipantRole } from "./config";

type Options = {
  enabled?: boolean;
  /** Identities on the trainer middle stage (always subscribe cams). */
  stageIdentities?: readonly string[];
  /** Identities in the right Participants rail (subscribe cam only if cam on). */
  railIdentities?: readonly string[];
  /** Hard cap so a bad list cannot subscribe hundreds of cams. */
  maxCameraSubscriptions?: number;
};

function uniqueKey(ids: readonly string[]): string {
  return Array.from(new Set(ids.filter(Boolean)))
    .sort()
    .join("\0");
}

export function useSelectiveTrackSubscription({
  enabled = true,
  stageIdentities = [],
  railIdentities = [],
  maxCameraSubscriptions = 24,
}: Options = {}) {
  const room = useMaybeRoomContext();
  const speakers = useSpeakingParticipants();
  const stageKey = useMemo(
    () => uniqueKey(stageIdentities),
    [stageIdentities],
  );
  const railKey = useMemo(() => uniqueKey(railIdentities), [railIdentities]);

  useEffect(() => {
    if (!enabled || !room) return;

    const onStage = new Set(
      stageKey ? stageKey.split("\0").filter(Boolean) : [],
    );
    const onRail = new Set(railKey ? railKey.split("\0").filter(Boolean) : []);

    const sync = () => {
      const speakingIds = new Set(
        speakers.filter((s) => !s.isLocal).map((s) => s.identity),
      );
      const remotes = Array.from(room.remoteParticipants.values());

      // Prefer stage slots, then rail cams that are on, then cap.
      const cameraWant = new Set<string>();
      Array.from(onStage).forEach((id) => {
        if (cameraWant.size >= maxCameraSubscriptions) return;
        cameraWant.add(id);
      });
      for (const p of remotes) {
        if (cameraWant.size >= maxCameraSubscriptions) break;
        if (!onRail.has(p.identity)) continue;
        if (onStage.has(p.identity)) continue;
        if (p.isCameraEnabled) cameraWant.add(p.identity);
      }

      for (const p of remotes) {
        const role = parseParticipantRole(p.metadata, p.identity);
        const isTrainer = role === "trainer";
        const isPresenter = isParticipantPresenter(p.metadata);
        const isSpeaking = speakingIds.has(p.identity);
        const wantCamera =
          cameraWant.has(p.identity) || isTrainer || isPresenter;
        const wantAudio =
          wantCamera || isSpeaking || isTrainer || isPresenter;

        for (const pub of p.getTrackPublications()) {
          if (!pub.trackSid) continue;
          if (!(pub instanceof RemoteTrackPublication)) continue;
          const isScreen = pub.source === Track.Source.ScreenShare;
          const isAudio = pub.kind === Track.Kind.Audio;
          const isCamera = pub.source === Track.Source.Camera;
          const subscribe =
            isScreen ||
            (isAudio && wantAudio) ||
            (isCamera && wantCamera);

          if (pub.isSubscribed !== subscribe) {
            try {
              pub.setSubscribed(subscribe);
            } catch {
              /* ignore race on disconnect */
            }
          }
        }
      }
    };

    sync();
    room.on(RoomEvent.TrackPublished, sync);
    room.on(RoomEvent.TrackUnpublished, sync);
    room.on(RoomEvent.ParticipantConnected, sync);
    room.on(RoomEvent.ParticipantDisconnected, sync);
    room.on(RoomEvent.ActiveSpeakersChanged, sync);
    room.on(RoomEvent.TrackMuted, sync);
    room.on(RoomEvent.TrackUnmuted, sync);
    return () => {
      room.off(RoomEvent.TrackPublished, sync);
      room.off(RoomEvent.TrackUnpublished, sync);
      room.off(RoomEvent.ParticipantConnected, sync);
      room.off(RoomEvent.ParticipantDisconnected, sync);
      room.off(RoomEvent.ActiveSpeakersChanged, sync);
      room.off(RoomEvent.TrackMuted, sync);
      room.off(RoomEvent.TrackUnmuted, sync);
    };
  }, [enabled, room, speakers, stageKey, railKey, maxCameraSubscriptions]);
}
